import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useDisasterObjContext } from "../../../context/DisasterObjContext";
import { useAuth } from "../../../../src/components/AuthContext";
import HeaderTitle from "../../../components/HeaderTitle";
import CustomInput from "../../../components/CustomInput";
import { closingSections } from "../../datasource";
import DisasterMgtService from "../../../service/citizen/DisasterMgtService";

const QuestionAccordion = ({
  question,
  questionKey,
  responses,
  onResponseChange,
  register,
  errors,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(
    responses[questionKey]?.answer || ""
  );
  const [description, setDescription] = useState(
    responses[questionKey]?.description || ""
  );

  // Update local state when responses prop changes
  useEffect(() => {
    setSelectedAnswer(responses[questionKey]?.answer || "");
    setDescription(responses[questionKey]?.description || "");
  }, [responses, questionKey]);

  const handleRadioChange = (value) => {
    setSelectedAnswer(value);
    onResponseChange(questionKey, {
      answer: value,
      description: description,
    });
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    // Only update local state, don't save yet
  };

  const handleDescriptionBlur = () => {
    // Save when user finishes typing and moves away from field
    onResponseChange(questionKey, {
      answer: selectedAnswer,
      description: description,
    });
  };

  return (
    <div className="box-border flex w-full flex-col overflow-hidden rounded-mini border border-gainsboro-300 px-2">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex w-full items-start justify-between gap-x-8 bg-transparent py-2 text-start text-sm font-medium leading-5 text-darkslategray-100 hover:cursor-pointer"
      >
        <div className="flex flex-col gap-1">
          <div>
            <p className="text-sm font-medium text-black">{question}</p>
          </div>
          <p className="text-sm font-semibold text-black">
            {selectedAnswer ? (
              selectedAnswer
            ) : (
              <span className="text-dark-burgundy">No</span>
            )}
            {responses[questionKey]?.description &&
              `, ${responses[questionKey]?.description}`}
          </p>
        </div>
        <svg
          className={`flex-shrink-0 transform transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          width={16}
          height={16}
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="mb-2 mt-2 px-2">
          <div className="mb-4 space-y-2">
            <label className="flex cursor-pointer items-center">
              <input
                className="form-radio h-5 min-h-[1.25rem] w-5 min-w-[1.25rem] appearance-none border-gray-300 text-blue-600"
                type="radio"
                name={questionKey}
                value="Yes"
                checked={selectedAnswer === "Yes"}
                onChange={() => handleRadioChange("Yes")}
              />
              <span className="ml-2 text-sm text-gray-800">Yes</span>
            </label>

            <label className="flex cursor-pointer items-center">
              <input
                className="form-radio h-5 min-h-[1.25rem] w-5 min-w-[1.25rem] appearance-none border-gray-300 text-blue-600"
                type="radio"
                name={questionKey}
                value="No"
                checked={selectedAnswer === "No"}
                onChange={() => handleRadioChange("No")}
              />
              <span className="ml-2 text-sm text-gray-800">No</span>
            </label>
          </div>

          <div className="mt-4">
            <CustomInput
              errors={errors}
              name={`${questionKey}_description`}
              placeholder="Description (Optional)"
              disabled={!selectedAnswer}
              value={description}
              register={register(`${questionKey}_description`, {
                onChange: handleDescriptionChange,
                onBlur: handleDescriptionBlur,
                disabled: !selectedAnswer,
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ScreeningClosing = ({ activeDisasters, accessPermissions }) => {
  const [responses, setResponses] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [existingRecords, setExistingRecords] = useState([]);

  const { disaster } = useDisasterObjContext();
  const { userId, userData, employeeDetails } = useAuth();
  const accessedUserId = sessionStorage.getItem("accessedUserId");

  // Try to get user ID from multiple sources
  const currentUserId =
    userId ||
    userData?.id ||
    sessionStorage.getItem("userId") ||
    employeeDetails?.id;

  const {
    register,
    formState: { errors },
    watch,
  } = useForm();

  // Function to generate section ID from title
  const generateSectionId = (title) => {
    return (
      title.toLowerCase().replace(/\s+/g, "").replace(/&/g, "") + "section"
    );
  };

  const handleResponseChange = (questionKey, responseData) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: responseData,
    }));

    // Save immediately without debouncing
    saveQuestionResponse(questionKey, responseData);
  };

  const generateRandomId = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  // Load existing assistance records and populate responses
  const loadExistingRecords = useCallback(async () => {
    if (!disaster || !accessedUserId) return;

    try {
      // Get existing General records to find Immediate Needs record
      const existingRecords = await DisasterMgtService.fetchRecords(
        accessedUserId,
        "general"
      );

      // Find the Immediate Needs record for this disaster
      const immediateNeedsRecord = existingRecords.find(
        (record) =>
          record.sub_category === "Immediate Needs" &&
          !record.is_deleted &&
          record.associatedDisasters.includes(disaster.id)
      );

      const existingResponses = {};

      if (immediateNeedsRecord && immediateNeedsRecord.assistanceDetails) {
        // Load existing assistance details and map them to question responses
        immediateNeedsRecord.assistanceDetails
          .filter((detail) => !detail.is_deleted && detail.need_type)
          .forEach((detail) => {
            existingResponses[detail.need_type] = {
              answer: "Yes",
              description: detail.description || "",
            };
          });
      }

      setExistingRecords(immediateNeedsRecord ? [immediateNeedsRecord] : []);
      // Pre-populate responses with existing data
      setResponses(existingResponses);
    } catch (error) {
      console.error("Error loading existing records:", error);
    }
  }, [disaster, accessedUserId]);

  // Load existing records on component mount
  useEffect(() => {
    loadExistingRecords();
  }, [loadExistingRecords]);

  const saveQuestionResponse = async (questionKey, responseData = null) => {
    if (!disaster || !accessedUserId || !currentUserId || !questionKey) {
      console.error("Missing required data for saving question response");
      return;
    }

    const response = responseData || responses[questionKey];
    if (!response || !response.answer) {
      console.log("No response to save for question:", questionKey);
      return;
    }

    setIsSaving(true);

    try {
      const userRole =
        employeeDetails?.role || userData?.user_role || "Unknown";

      // Get existing General records to find or create Immediate Needs record
      const existingRecords = await DisasterMgtService.fetchRecords(
        accessedUserId,
        "general"
      );

      // Find the Immediate Needs record for this disaster
      let immediateNeedsRecord = existingRecords.find(
        (record) =>
          record.sub_category === "Immediate Needs" &&
          !record.is_deleted &&
          record.associatedDisasters.includes(disaster.id)
      );

      // If no Immediate Needs record exists, create one
      if (!immediateNeedsRecord) {
        const currentDate = new Date();
        const newRecordData = {
          generalID: generateRandomId(),
          sub_category: "Immediate Needs",
          damage: "Immediate Needs",
          description: "Immediate Needs",
          editor_ID: currentUserId,
          editor_type: userRole,
          is_deleted: false,
          last_edited: currentDate,
          date_declared: currentDate,
          date_entered: currentDate,
          estimate: 0,
          associatedDisasters: [disaster.id],
          assistanceDetails: [],
        };

        await DisasterMgtService.addRecord(
          accessedUserId,
          newRecordData,
          "general"
        );
        immediateNeedsRecord = newRecordData;
        console.log("Created new Immediate Needs record");
      }

      // Get current assistance details or initialize empty array
      let assistanceDetails = Array.isArray(
        immediateNeedsRecord.assistanceDetails
      )
        ? immediateNeedsRecord.assistanceDetails
        : [];

      // Get source and organization based on user role
      let defaultSource = "";
      let defaultOrganization = "";

      if (userRole === "citizen" && userData?.fullName) {
        defaultSource = userData.fullName;
      } else if (userRole === "center" && userData?.orgName) {
        defaultSource = userData.orgName;
      } else if (userRole === "partner" && userData?.hubName) {
        defaultSource = userData.hubName;
      }

      defaultOrganization = defaultSource;

      // Find existing assistance detail for this question
      const existingDetailIndex = assistanceDetails.findIndex(
        (detail) => detail.need_type === questionKey
      );

      if (response.answer === "No") {
        // Remove or mark as deleted
        if (existingDetailIndex !== -1) {
          assistanceDetails[existingDetailIndex].is_deleted = true;
          console.log(`Question response deleted: ${questionKey}`);
        }
      } else if (response.answer === "Yes") {
        // Create or update assistance detail
        const currentDate = new Date();
        const formattedDate = `${(currentDate.getMonth() + 1)
          .toString()
          .padStart(2, "0")}/${currentDate
          .getDate()
          .toString()
          .padStart(2, "0")}/${currentDate.getFullYear()}`;

        const assistanceDetail = {
          id:
            existingDetailIndex !== -1
              ? assistanceDetails[existingDetailIndex].id
              : generateRandomId(),
          description: response.description || "",
          need_type: questionKey,
          source: defaultSource,
          organization: defaultOrganization,
          date_assistance: formattedDate,
          amount: 0,
          editor_ID: currentUserId,
          editor_type: userRole,
          is_deleted: false,
          photo_details: [],
        };

        if (existingDetailIndex !== -1) {
          // Update existing detail
          assistanceDetails[existingDetailIndex] = assistanceDetail;
          console.log(`Question response updated: ${questionKey}`);
        } else {
          // Add new detail
          assistanceDetails.push(assistanceDetail);
          console.log(`Question response created: ${questionKey}`);
        }
      }

      // Calculate total amount
      const totalAmount = assistanceDetails
        .filter((detail) => !detail.is_deleted)
        .reduce((sum, detail) => sum + (detail.amount || 0), 0);

      // Update the General record with new assistance details
      await DisasterMgtService.updateRecord(
        accessedUserId,
        immediateNeedsRecord.generalID,
        {
          assistanceDetails: assistanceDetails,
          estimate: totalAmount,
          value: totalAmount,
          last_edited: new Date(),
        },
        "general"
      );
    } catch (error) {
      console.error("Error saving question response:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full">
      {isSaving && (
        <div className="mb-4 flex items-center justify-end text-sm text-blue-600">
          <svg className="animate-spin mr-2 h-4 w-4" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Saving...
        </div>
      )}

      {closingSections.map((section, sectionIndex) => (
        <div key={`section-${sectionIndex}`} className="mb-6">
          <HeaderTitle
            title={section.title}
            id={generateSectionId(section.title)}
          />

          <div className="mt-4 flex flex-wrap gap-4 text-xs text-darkslategray-100">
            {section.questions.map((question) => (
              <div
                key={question.key}
                className="flex w-[calc(33.333%-11px)] mq1125:w-[calc(50%-8px)] mq800:w-full"
              >
                <QuestionAccordion
                  question={question.text}
                  questionKey={question.key}
                  responses={responses}
                  onResponseChange={handleResponseChange}
                  register={register}
                  errors={errors}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScreeningClosing;
